import React from 'react';
import { BillIcon, CashIcon, PettyCashBudgetIcon, Spinner, useToast } from '@razorpay/blade/components';
import { useUserId } from 'components/EmployeeDetails/BankAccountDetails/hooks/useUserId';
import { getErrorsAsString } from 'components/Kyc/utils';
import useFetchCompensationDetails from 'components/PayDetails/hooks/useFetchCompensationDetails';
import { format } from 'date-fns';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import ComponentContainer from 'components/PayDetails/components/generics/ComponentContainer';
import ComponentHeader from 'components/PayDetails/components/generics/ComponentHeader';
import ComponentSection from 'components/PayDetails/components/generics/ComponentSection';
import ComponentSectionItem from 'components/PayDetails/components/generics/ComponentSectionItem';
import ComponentTable from 'components/PayDetails/components/generics/CtcBreakdownTable';

export const RearchSalaryStructurePreview = ({ annualCtc }: { annualCtc: Number }) => {
  const userId = useUserId();
  const { isMileStone1Enabled: isRearchEnabled } = useIsFeatureDisabledForRearch();
  const toast = useToast();
  const payrollMonth = format(Date.now(), 'yyyy-MM-dd');
  const { data, isLoading } = useFetchCompensationDetails({
    queryKey: ['getEmployeeCompensationData', userId, payrollMonth],
    userId: String(userId),
    payrollMonth,
    userFilters: {
      includeZeroSalaryUsers: true,
    },
    userRevisions: [
      {
        revisionInfo: [
          {
            effectiveFromMonth: format(Date.now(), 'yyyy-MM'),
            fullCtc: Number(annualCtc) * 100,
            proratedCtc: Number(annualCtc) * 100,
          },
        ],
      },
    ],
    enabled: isRearchEnabled,

    onError: (e) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const ctcData = data?.ctcBreakdownData;

  if (isLoading) {
    return <Spinner accessibilityLabel="Loading" />;
  }

  if (!ctcData) return null;

  return (
    <ComponentContainer>
      {ctcData.fixed.data.length > 0 && (
        <ComponentSection>
          <ComponentHeader
            icon={<CashIcon size="medium" color="surface.icon.onSea.onSubtle" />}
            iconBackground={'surface.background.sea.subtle'}
            title={'Fixed'}
            description={'Monthly earning component'}
          />
          <ComponentSectionItem>
            <ComponentTable data={ctcData.fixed} />
          </ComponentSectionItem>
        </ComponentSection>
      )}

      {ctcData.variable.data.length > 0 && (
        <ComponentSection>
          <ComponentHeader
            icon={<PettyCashBudgetIcon size="medium" color="surface.icon.primary.normal" />}
            iconBackground={'surface.background.primary.subtle'}
            title={'Variable'}
            description={'Variable component based on performance, not part of monthly salary'}
          />
          <ComponentSectionItem>
            <ComponentTable data={ctcData.variable} showMonthlyColumn={false} />
          </ComponentSectionItem>
        </ComponentSection>
      )}

      {ctcData.otherCtc.data.length > 0 && (
        <ComponentSection>
          <ComponentHeader
            icon={<BillIcon size="medium" color="surface.icon.gray.normal" />}
            iconBackground={'surface.background.gray.intense'}
            title={'Other CTC component'}
            description={'Components part of CTC but not directly payable to the employee'}
          />
          <ComponentSectionItem>
            <ComponentTable data={ctcData.otherCtc} showMonthlyColumn={false} />
          </ComponentSectionItem>
        </ComponentSection>
      )}
    </ComponentContainer>
  );
};
